/**
 * One company, for EVT platform administrators: its sign-in settings, which
 * flows it has (`TenantFlowsCard`), what it still needs before its people can
 * run flows (readiness, checked live against fr-docs), and its people.
 * Suspending a company signs everyone out; the page confirms that inline before
 * doing it.
 */
import { Badge, Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import type { AccessTenant } from '@portal/contracts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getAccessTenant, getTenantReadiness, updateTenant } from '../../api/access'
import { useSession } from '../../auth/useSession'
import { AccessPanel, accessKey, parseEmailList } from '../../components/access/AccessPanel'
import { TenantFlowsCard } from '../../components/access/TenantFlowsCard'
import { describeApiError, useI18n } from '../../i18n'
import { platformTenantsKey } from './TenantsPage'

export function TenantPage() {
  const { tenantId = '' } = useParams()
  const { t } = useI18n()
  const { platformAdmin, strongAuth } = useSession()
  const enabled = platformAdmin && strongAuth && Boolean(tenantId)
  const tenant = useQuery({
    queryKey: accessKey(tenantId, 'tenant'),
    queryFn: () => getAccessTenant(tenantId),
    enabled
  })

  if (!platformAdmin) {
    return (
      <div className="page portal-page">
        <Notice kind="warning">{t.errors.accessForbidden}</Notice>
      </div>
    )
  }

  return (
    <div className="page portal-page access-page">
      <Link className="access-back" to="/platform/tenants">
        {t.access.backToTenants}
      </Link>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {tenant.data?.name ?? tenantId}{' '}
            {tenant.data?.status === 'suspended' && <Badge kind="disabled">{t.access.statusSuspended}</Badge>}
          </h1>
          <div className="page-sub mono">{tenantId}</div>
        </div>
      </div>
      {!strongAuth ? (
        <Notice kind="info">{t.access.stepUp}</Notice>
      ) : tenant.isLoading ? (
        <Spinner size={16} />
      ) : tenant.error ? (
        <Notice kind="danger">{describeApiError(t, tenant.error)}</Notice>
      ) : tenant.data ? (
        <>
          <SettingsCard tenant={tenant.data} />
          <TenantFlowsCard tenantId={tenantId} />
          <ReadinessCard tenantId={tenantId} />
          <AccessPanel tenantId={tenantId} tenant={tenant.data} />
        </>
      ) : null}
    </div>
  )
}

function SettingsCard({ tenant }: { tenant: AccessTenant }) {
  const { t } = useI18n()
  const qc = useQueryClient()
  const [name, setName] = useState(tenant.name)
  const [emailCode, setEmailCode] = useState(tenant.settings.emailCodeLogin)
  const [domains, setDomains] = useState(tenant.settings.allowedEmailDomains.join(', '))
  const [busy, setBusy] = useState(false)
  const [confirmSuspend, setConfirmSuspend] = useState(false)
  const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null)

  // A save elsewhere (another tab, another administrator) re-seeds the form.
  useEffect(() => {
    setName(tenant.name)
    setEmailCode(tenant.settings.emailCodeLogin)
    setDomains(tenant.settings.allowedEmailDomains.join(', '))
  }, [tenant])

  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: accessKey(tenant.id, 'tenant') }),
      qc.invalidateQueries({ queryKey: accessKey(tenant.id, 'audit') }),
      qc.invalidateQueries({ queryKey: accessKey(tenant.id, 'members') }),
      qc.invalidateQueries({ queryKey: ['portal', 'platform'] }),
      qc.invalidateQueries({ queryKey: platformTenantsKey })
    ])

  const run = async (change: Parameters<typeof updateTenant>[1], done: string) => {
    setBusy(true)
    setMessage(null)
    try {
      await updateTenant(tenant.id, change)
      setMessage({ kind: 'success', text: done })
      setConfirmSuspend(false)
      await refresh()
    } catch (err) {
      setMessage({ kind: 'danger', text: describeApiError(t, err) })
    } finally {
      setBusy(false)
    }
  }

  const onSave = (e: FormEvent) => {
    e.preventDefault()
    void run(
      {
        name: name.trim(),
        settings: { emailCodeLogin: emailCode, allowedEmailDomains: parseEmailList(domains) }
      },
      t.access.saved
    )
  }

  return (
    <section className="card">
      <div className="card-head">
        <div className="card-title">{t.access.settingsTitle}</div>
      </div>
      <form className="card-pad access-invite" onSubmit={onSave}>
        <div className="access-invite-row">
          <label className="field">
            <span className="field-label">{t.access.fieldName}</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field-label">{t.access.fieldDomains}</span>
            <input
              className="input"
              value={domains}
              placeholder="heineken.com.br"
              onChange={(e) => setDomains(e.target.value)}
            />
          </label>
        </div>
        <p className="muted access-help">{t.access.fieldDomainsHelp}</p>
        <label className="access-check">
          <input type="checkbox" checked={emailCode} onChange={(e) => setEmailCode(e.target.checked)} />
          <span>{t.access.fieldEmailCode}</span>
        </label>
        <p className="muted access-help">{t.access.fieldEmailCodeHelp}</p>
        <div className="access-settings-actions">
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !name.trim()}>
            {busy ? <Spinner size={13} /> : <Icon name="check" size={13} />} {t.access.saveSettings}
          </button>
          {tenant.status === 'suspended' ? (
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => void run({ status: 'active' }, t.access.saved)}
            >
              {t.access.activate}
            </button>
          ) : !confirmSuspend ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm access-danger"
              disabled={busy}
              onClick={() => setConfirmSuspend(true)}
            >
              {t.access.suspend}
            </button>
          ) : null}
        </div>
        {confirmSuspend && (
          <div className="access-confirm">
            <span>{t.access.confirmSuspend(tenant.name)}</span>
            <div className="access-actions">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={busy}
                onClick={() => void run({ status: 'suspended' }, t.access.saved)}
              >
                {busy ? <Spinner size={12} /> : null} {t.access.confirm}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmSuspend(false)}>
                {t.common.cancel}
              </button>
            </div>
          </div>
        )}
        {message && <Notice kind={message.kind}>{message.text}</Notice>}
      </form>
    </section>
  )
}

function ReadinessCard({ tenantId }: { tenantId: string }) {
  const { t } = useI18n()
  const readiness = useQuery({
    queryKey: ['portal', 'platform', 'readiness', tenantId],
    queryFn: () => getTenantReadiness(tenantId)
  })
  const r = readiness.data

  return (
    <section className="card">
      <div className="card-head access-members-head">
        <div className="card-title">{t.access.readinessTitle}</div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={readiness.isFetching}
          onClick={() => void readiness.refetch()}
        >
          {readiness.isFetching ? <Spinner size={12} /> : <Icon name="refresh" size={12} />} {t.access.recheck}
        </button>
      </div>
      <div className="card-pad access-invite">
        <p className="muted access-help">{t.access.readinessHelp}</p>
        {readiness.error ? (
          <Notice kind="danger">{describeApiError(t, readiness.error)}</Notice>
        ) : !r ? (
          <Spinner size={14} />
        ) : (
          <>
            <ul className="access-readiness">
              {r.checks.map((c) => (
                <li key={c.key}>
                  <Icon name={c.ok ? 'check' : 'x'} size={14} className={c.ok ? 'is-ok' : 'is-missing'} />
                  <div>
                    <div>{t.access.checks[c.key] ?? c.key}</div>
                    {!c.ok && <div className="muted access-sub">{t.access.checkFix[c.key]}</div>}
                    {!c.ok && c.key === 'signing_accepted' && r.signing === 'unreachable' && r.signingDetail && (
                      <div className="access-warn">{t.access.signingUnreachable(r.signingDetail)}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <p className="muted access-help">
              {r.flows === null
                ? t.access.flowsUnknown
                : r.flows.length > 0
                  ? t.access.flowsVisible(r.flows.map((f) => f.name).join(', '))
                  : null}
            </p>
          </>
        )}
      </div>
    </section>
  )
}
